(function () {
    'use strict';

    angular
        .module('app')
        .directive('drvEntityLinkedManyInput', drvEntityLinkedManyInput);

    /**
     * @ngdoc directive
     * @name drvEntityLinkedManyInput
     * @description Many Linked Entity input control. This will support entity previewing and ordering.
     *
     * @param {array} ngModel - An array of {number}, each one an entity id.
     * @param {object} entityTemplate - An entity template object. When the user clicks 'Add', this is the type of entities they'll be shown to select from.
     * @param {bool} loadUserEntities - Optional, default false. If true, instead of using entities which belong to the entity template, the selection list will be users.
     * @param {object} field - An "entity field" object with properties:
     *   - {string} Name
     *   - {bool} DisableFullClear
     *   - {bool} Disabled
     *   - {bool} Mandatory
     */
    function drvEntityLinkedManyInput() {
        return {
            templateUrl: 'app/fieldinput/entity-linked-many-input.tmpl.html',
            require: 'ngModel',
            scope: {},
            controller: drvEntityLinkedManyInputCtrl,
            controllerAs: 'drvCtrl',
            bindToController: {
                value: '=ngModel',
                entityTemplate: '<',
                loadUserEntities: '<?',
                field: '<'
            },
            link: link
        };
    }

    drvEntityLinkedManyInputCtrl.$inject = [
        '$q',
        '$scope',
        'DialogService',
        'EntitiesService',
        'EntityDialogService'];

    function drvEntityLinkedManyInputCtrl(
        $q,
        $scope,
        DialogService,
        EntitiesService,
        EntityDialogService) {
        /* jshint validthis:true */
        var drvCtrl = this;
        var userEntitiesLoaded = false;
        var entitiesCache = [];
        Object.defineProperty(drvCtrl, 'linkedEntities', { get: getLinkedEntitiesList });
        drvCtrl.addLinkedEntity = addLinkedEntity;
        drvCtrl.deleteEntityLink = deleteEntityLink;
        drvCtrl.validateEntity = validateEntity;
        drvCtrl.getUsersValid = getUsersValid;

        activate();

        function activate() {
            loadEntities()
                .finally(function () {
                    $scope.$watch('drvCtrl.value', function (newVal, oldVal) {
                        if (newVal !== oldVal) {
                            loadEntities();
                        }
                    });
                });
        }

        /**
         * Load the entities required to show the linked enities list. A cache internal to this directive is used to increase performance.
         */
        function loadEntities() {
            drvCtrl.loading = true;
            var loadPromises = [];

            if (!_.isNil(drvCtrl.value)) {
                if (drvCtrl.loadUserEntities && !drvCtrl.userEntitiesLoaded) {
                    loadPromises.push(updateAvailableUserList());
                }

                angular.forEach(_.uniq(drvCtrl.value), function (entityId) {
                    if (!_.some(entitiesCache, function (entity) {
                        return entity.Id === entityId;
                    })) {
                        loadPromises.push(loadEntity(entityId));
                    }
                });
            }

            return $q.all(loadPromises)
                .then(function () {
                    drvCtrl.loading = false;
                }, function () {
                    drvCtrl.loading = false;
                });
        }

        /**
         * Load entity for the supplied id.
         *
         * @param {int} entityId - Identifier for an entity.
         */
        function loadEntity(entityId) {
            if (!_.some(entitiesCache, function (entity) {
                return entity.Id === entityId;
            })) {
                return EntitiesService.getPreviewText(entityId).then(getEntityOk);
            }

            function getEntityOk(data) {
                if (!_.isNil(data)) {
                    entitiesCache.push(data);
                } else {
                    entitiesCache.push({ Id: entityId, Error: '<span class="error-messsage-div">Unable to load preview for ' + drvCtrl.entityTemplate.Name + ' with id of ' + entityId + '.</span>' });
                }
            }
        }

        /**
         * Get the list of linked entities which will be shown on the view.
         * This list will be ordered and linked entities which were unable to be loaded will have an appropriate message as their preview text (this will be shown to the user)
         *
         * @return {array} Return a list of linked entities
         */
        function getLinkedEntitiesList() {
            var linkedEntityList = [];

            angular.forEach(drvCtrl.value, function (entityId) {
                var linkedEntity = _.find(entitiesCache, function (entity) {
                    return entity.Id === entityId;
                });

                if (_.isNil(linkedEntity)) {
                    if (!_.isNil(drvCtrl.entityTemplate)) {
                        linkedEntityList.push({ Id: entityId, Error: 'Unable to load preview for ' + drvCtrl.entityTemplate.Name + ' with id of ' + entityId + '.' });
                    } else {
                        linkedEntityList.push({ Id: entityId, Error: 'Unable to load preview for item with id of ' + entityId + '.' });
                    }
                } else {
                    linkedEntityList.push(linkedEntity);
                }
            });

            linkedEntityList = orderLinkedEntities(linkedEntityList);

            if (!angular.equals(linkedEntityList, getLinkedEntitiesList.entityList)) {
                getLinkedEntitiesList.entityList = linkedEntityList;
            }

            return getLinkedEntitiesList.entityList;
        }

        /**
         * Get the ordered list of linked entities.
         *
         * @param {array} entities - List of linked entities which are to be ordered.
         * @return {array} Return a list of ordered linked entities.
         */
        function orderLinkedEntities(entities) {
            return _.sortByOrder(entities, ['PreviewNameText', 'PreviewDescriptionText'], ['asc', 'asc']);
        }

        /**
         * Show a dialog to select entities which can be linked.
         */
        function addLinkedEntity() {
            if (!_.isNil(drvCtrl.entityTemplate)) {
                var pageHeading = 'Select ' + drvCtrl.entityTemplate.PluralName;

                DialogService.show({
                    locals: {
                        pageHeading: pageHeading,
                        entityTemplate: drvCtrl.entityTemplate,
                        loadUserEntities: drvCtrl.loadUserEntities,
                        multiSelectMode: true
                    },
                    controller: 'EntitySelectorDialogCtrl',
                    controllerAs: 'vm',
                    templateUrl: 'app/controls/entity/entity-selector-dialog.tmpl.html',
                    size: DialogService.sizes.extraLarge
                })
                .then(editFieldOk);
            } else {
                DialogService.show({
                    locals: {
                    },
                    controller: 'EntitySelectorViaTemplateDialogCtrl',
                    controllerAs: 'vm',
                    templateUrl: 'app/controls/entity/entity-selector-via-template-dialog.tmpl.html',
                    size: DialogService.sizes.extraLarge
                })
                .then(function (result) { editFieldOk([result]); });
            }

            function editFieldOk(entityIds) {
                if (_.isNil(drvCtrl.value)) {
                    drvCtrl.value = [];
                }

                var linkedEntityList = [];

                angular.forEach(entityIds, function (entityId) {
                    if (!_.find(drvCtrl.value, function (id) { return entityId === id; })) {
                        linkedEntityList.push(entityId);
                    }
                });

                drvCtrl.value = drvCtrl.value.concat(linkedEntityList);
            }
        }

        /**
         * Delete the supplied entity link. If there are duplicates of the entity link, they will all be removed.
         *
         * @param {int} linkedEntityId - Identifier of the entity which should be removed from the linked list.
         */
        function deleteEntityLink(linkedEntityId) {
            EntityDialogService.confirmDeleteEntities(linkedEntityId, drvCtrl.entityTemplate)
                .then(function () {
                    _.remove(drvCtrl.value, function (entityId) {
                        return entityId === linkedEntityId;
                    });
                });
        }

        /*
         * Update the available user list, utilising the usersForEntityTemplateId property for filtering
         *
         * @returns {promise} - Returns a promise that resolves once the users have been retrieved
         */
        function updateAvailableUserList() {
            drvCtrl.loadingAvailableUsers = true;

            return EntitiesService.query({
                skipSecurity: true,
                previewTextOnly: true,
                userIsGrantedEntityTemplateId: drvCtrl.usersForEntityTemplateId,
                EntityType: 'User'
            })
            .then(getUserEntitiesOk, getUserEntitiesError)
            .finally(function () {
                drvCtrl.loadingAvailableUsers = false;
            });

            function getUserEntitiesOk(data) {
                drvCtrl.userEntities = data.Entities;
                userEntitiesLoaded = true;
            }

            function getUserEntitiesError(response) {
                DialogService.error('Error fetching Users', response);
            }
        }

        /*
         * Check if the supplied entity is valid
         * Validation for User entities - If loadUserEntities is true, the entity must be in the list of users that have access to the supplied entity template id if userForEntityTemplateId has been set.
         *
         * @returns {bool} - True if the entity is valid
         */
        function validateEntity(entity) {
            return validateUser(entity);
        }

        /*
         * Check if the supplied user entity is valid
         * Validation - If loadUserEntities is true, the entity must be in the list of users that have access to the supplied entity template id if userForEntityTemplateId has been set.
         *
         * @returns {bool} - True if the user entity is valid
         */
        function validateUser(entity) {
            var result = true;

            if (drvCtrl.loadUserEntities) {
                result = _.some(drvCtrl.userEntities, function (userEntity) {
                    return userEntity.Id === entity.Id;
                });
            }

            return result;
        }

        /*
         * Check if the user entities are valid
         *
         * @returns {bool} - True if the user entities are valid
         */
        function getUsersValid() {
            var result = true;

            if (!_.isNil(drvCtrl.linkedEntities) && drvCtrl.loadUserEntities) {
                angular.forEach(drvCtrl.linkedEntities, function (linkedEntity) {
                    if (result) {
                        result = validateUser(linkedEntity);
                    }
                });
            }

            return result;
        }
    }

    /*
     * Link function which will handle validation
     */
    function link($scope, $element, $attrs, $ctrl) {
        $ctrl.$name = $scope.drvCtrl.field.Name;
        // Set allowInvalid option to be true. If this is not done, the $scope.$watch which trigger validation will cause infinite digest.
        $ctrl.$options = { allowInvalid: true};
        $ctrl.$validators.required = function required(modelValue, viewValue) {
            return !($scope.drvCtrl.field.Mandatory && (_.isNil(modelValue) || modelValue.length < 1));
        };
        $ctrl.$validators.userValid = function required(modelValue, viewValue) {
            return $scope.drvCtrl.getUsersValid();
        };

        // If the value has been changed, the validation needs to run
        $scope.$watch('drvCtrl.value', function (newVal, oldVal) {
            if (newVal !== oldVal) {
                $ctrl.$validate();
            }
        },
        true);

        // If the user entities list has been updated (possibly due to selecting an entity template which has different permissions), the validation needs to run
        $scope.$watch('drvCtrl.userEntities', function (newVal, oldVal) {
            if (newVal !== oldVal) {
                $ctrl.$validate();
            }
        },
        true);
    }
})();